import { useState } from "react";
import { siteMeta } from "@/mocks/chigua";
import useSeo from "@/hooks/useSeo";
import Breadcrumbs from "@/components/chigua/Breadcrumbs";
import JsonLd from "@/components/chigua/JsonLd";

const FORM_ADDR = "https://readdy.ai/api/form/daens81tto0ceqqnh0v0";

const topics = ["线索投稿", "意见反馈", "栏目合作", "内容纠错", "其他"];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "联系我们-线索投稿与意见反馈-今日吃瓜",
    description:
      "联系今日吃瓜编辑部：提交圈内公开线索、意见反馈、内容纠错或栏目合作。我们只按公开材料整理内幕消息与内幕真相，欢迎来信一起把时间线拼完整。",
    path: "/contact",
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们-今日吃瓜",
    description: "今日吃瓜编辑部的线索投稿与意见反馈入口。",
    url: `${siteMeta.domain}/contact`,
    inLanguage: "zh-CN",
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honey = ((fd.get("company_alt") as string) || "").trim();
    setFormError("");
    if (honey) {
      setStatus("success");
      setMessage("已收到您的来信，我们将尽快处理，感谢您的反馈。");
      form.reset();
      return;
    }
    fd.delete("company_alt");

    setStatus("loading");
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage("已收到您的来信，感谢反馈，我们将尽快处理。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch (err) {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  }

  return (
    <div className="mx-auto max-w-[1180px] px-4 md:px-6">
      <JsonLd data={schema} />
      <Breadcrumbs items={[{ label: "联系我们" }]} />

      <header className="mx-auto max-w-3xl text-center">
        <span className="inline-flex items-center gap-1.5 rounded bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
          <i className="ri-mail-send-line" aria-hidden="true" />
          线索投稿 · 意见反馈
        </span>
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">联系我们</h1>
        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-7 text-foreground-600">
          无论是掌握与圈内事件相关的<strong className="font-semibold text-foreground-900">公开线索</strong>，还是对某篇
          <strong className="font-semibold text-foreground-900">内幕深扒复盘</strong>提出指正，都欢迎来信。我们会认真阅读每一份内容，并尽量在 3 个工作日内回复。
        </p>
      </header>

      <div className="mt-10 grid gap-8 pb-16 lg:grid-cols-12">
        {/* 左侧信息 */}
        <aside className="space-y-5 lg:col-span-4">
          <div className="rounded-xl border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">编辑部联系方式</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-700">
              <li className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">投稿与商务邮箱</span>
                  <a href={`mailto:${siteMeta.email}`} className="font-medium text-primary-700 underline-offset-2 hover:underline" rel="nofollow">
                    {siteMeta.email}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-time-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">编辑部值守</span>
                  每日 08:00 - 20:00
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-secure-payment-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">来信保障</span>
                  所有线索与来信严格保密，仅用于核实公开材料。
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">哪些内容适合投稿？</h2>
            <ul className="mt-4 space-y-2.5 text-sm leading-6 text-foreground-600">
              {[
                "可核验的官方公告与公开文件线索",
                "对既有复盘时间线的补充与纠错",
                "影视、音乐、综艺行业的幕后见闻（需可溯源）",
                "希望申请内容转载或栏目合作",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* 表单 */}
        <div className="lg:col-span-8">
          <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">在线来信</h2>
            <p className="mt-1 text-sm text-foreground-500">填写下方表单，内容将直达编辑部邮箱。带 * 为必填。</p>

            <form id="contact-form" data-readdy-form="daens81tto0ceqqnh0v0" className="mt-6 space-y-5" onSubmit={handleSubmit}>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    您的称呼 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="如何称呼您"
                    className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    联系邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  来信主题 <span className="text-accent-600">*</span>
                </label>
                <select
                  id="topic"
                  name="topic"
                  required
                  defaultValue={topics[0]}
                  className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition focus:border-primary-400"
                >
                  {topics.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  来信内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="请尽量附上可核验的公开来源，便于我们核实与整理。"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-3 text-sm leading-6 text-foreground-800 outline-none transition focus:border-primary-400"
                />
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              <div className="hp-field" aria-hidden="true">
                <label htmlFor="company_alt">公司名称</label>
                <input
                  id="company_alt"
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {status === "success" && (
                <div className="flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-100/70 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line text-secondary-700" aria-hidden="true" />
                  {message}
                </div>
              )}
              {status === "error" && (
                <div className="flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-error-warning-line text-accent-600" aria-hidden="true" />
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-[15px] font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                    正在发送…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" aria-hidden="true" />
                    提交来信
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}